import * as React from "react";

import { cn } from "@beweegbaas/ui/lib/utils";

function Table({
  className,
  containerClassName,
  ...props
}: React.ComponentProps<"table"> & {
  containerClassName?: string;
}) {
  return (
    <div
      data-slot="table-container"
      className={cn("scrollable-overlay relative w-full overflow-x-auto", containerClassName)}
    >
      <table
        data-slot="table"
        className={cn("w-full caption-bottom text-sm", className)}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("sticky top-0 z-10 backdrop-blur [&_tr]:border-b-[2px]", className)}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return <tbody data-slot="table-body" className={className} {...props} />;
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn("bg-muted/50 border-t font-medium [&>tr]:last:border-b-0", className)}
      {...props}
    />
  );
}

/** Elements inside a clickable row that keep their own click behaviour. */
const INTERACTIVE_SELECTOR = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  "label",
  "summary",
  "[contenteditable='true']",
  ...["button", "checkbox", "combobox", "link", "menuitem", "switch", "tab"].map(
    (role) => `[role='${role}']`,
  ),
].join(", ");

/** The link or checkbox in the row's `rowTarget` cell; the `row-actionable` variant mirrors it. */
const ROW_TARGET_SELECTOR = ":scope > [data-row-target] :is(a[href], [role='checkbox'])";

/** The element a click anywhere on the row activates, if the row has one. */
export function getTableRowTarget(row: Element): HTMLElement | null {
  return row.querySelector<HTMLElement>(ROW_TARGET_SELECTOR);
}

/**
 * Whether a click on the row belongs to something else: an inner control, a portal (menus and
 * popovers bubble through React but sit outside the row) or a text selection being made.
 */
function isClickForSomethingElse(event: React.MouseEvent<HTMLTableRowElement>): boolean {
  const row = event.currentTarget;
  const clicked = event.target;
  if (!(clicked instanceof Element) || !row.contains(clicked)) return true;
  const interactive = clicked.closest(INTERACTIVE_SELECTOR);
  if (interactive && row.contains(interactive)) return true;
  const selection = window.getSelection();
  return !!selection && !selection.isCollapsed && selection.containsNode(row, true);
}

function openInNewTab(target: HTMLElement): boolean {
  if (!(target instanceof HTMLAnchorElement)) return false;
  window.open(target.href, "_blank", "noopener");
  return true;
}

/**
 * Rows tint themselves from `--table-row-bg` / `--table-row-hover-bg` when a caller sets them
 * (status colours via `className`).
 *
 * A row with a `TableCell rowTarget` holding a link or checkbox is clickable as a whole: a click
 * on any other part of the row activates that link or checkbox, Ctrl/Cmd- and middle-click open
 * the link in a new tab, and it gets the pointer, hover and focus ring. The link stays the one
 * real anchor (and tab stop) of the row, so text in the row can still be selected.
 */
function TableRow({ className, onClick, onAuxClick, ...props }: React.ComponentProps<"tr">) {
  const handleClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
    onClick?.(event);
    if (event.defaultPrevented || event.button !== 0 || event.altKey || event.shiftKey) return;
    const target = getTableRowTarget(event.currentTarget);
    if (!target || isClickForSomethingElse(event)) return;
    if ((event.ctrlKey || event.metaKey) && openInNewTab(target)) return;
    target.click();
  };

  const handleAuxClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
    onAuxClick?.(event);
    if (event.defaultPrevented || event.button !== 1) return;
    const target = getTableRowTarget(event.currentTarget);
    if (!target || isClickForSomethingElse(event)) return;
    if (openInNewTab(target)) event.preventDefault();
  };

  return (
    <tr
      data-slot="table-row"
      className={cn(
        "border-border bg-(--table-row-bg) data-[state=selected]:bg-blue-50 dark:data-[state=selected]:bg-blue-950/50 border-b transition-colors",
        "row-actionable:cursor-pointer row-actionable:hover:bg-[var(--table-row-hover-bg,--alpha(var(--color-muted)/50%))]",
        "row-focus-visible:outline-ring row-focus-visible:outline-2 row-focus-visible:-outline-offset-2",
        className,
      )}
      onClick={handleClick}
      onAuxClick={handleAuxClick}
      {...props}
    />
  );
}

// Cells opting into `whitespace-normal` break between words: the app-wide
// `overflow-wrap: anywhere` would let the table squeeze them to a few letters wide.
function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "text-foreground h-10 px-4 text-left align-middle text-sm font-bold whitespace-nowrap [overflow-wrap:break-word] [&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    />
  );
}

function TableCell({
  className,
  rowTarget = false,
  ...props
}: React.ComponentProps<"td"> & {
  /** Its link or checkbox is what a click anywhere on the row activates (see `TableRow`). */
  rowTarget?: boolean;
}) {
  return (
    <td
      data-slot="table-cell"
      data-row-target={rowTarget ? "" : undefined}
      className={cn(
        "h-11 px-4 align-middle whitespace-nowrap [overflow-wrap:break-word] [&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    />
  );
}

/** Primary cell text with a muted context line underneath, instead of a separate column. */
function TableCellStack({
  className,
  secondary,
  children,
  ...props
}: React.ComponentProps<"div"> & { secondary?: React.ReactNode }) {
  return (
    <div
      data-slot="table-cell-stack"
      className={cn("flex min-w-0 flex-col items-start gap-0.5 py-2", className)}
      {...props}
    >
      {children}
      {secondary != null && secondary !== "" && (
        <span className="text-muted-foreground text-xs leading-4">{secondary}</span>
      )}
    </div>
  );
}

function TableCaption({ className, ...props }: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("text-muted-foreground mt-4 text-sm", className)}
      {...props}
    />
  );
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCellStack,
  TableCaption,
};
